
import { useAuth } from "@/contexts/auth";
import { useRoleChecks } from "./use-role-checks";
import { useAdminCountryAssignments } from "@/hooks/admin/countries/useAdminCountryAssignments";
import { useQuery } from "@tanstack/react-query";

/**
 * Hook for country-specific permission checks
 */
export const useCountryAccess = () => {
  const { profile } = useAuth();
  const { isSuperAdmin, isCountryAdmin } = useRoleChecks();
  const { getCurrentUserCountries } = useAdminCountryAssignments();
  
  // Get admin's assigned countries
  const { data: adminCountries = [] } = useQuery({
    queryKey: ['admin-countries', profile?.id],
    queryFn: getCurrentUserCountries,
    enabled: !!profile?.id && isCountryAdmin(),
  });
  
  // Check if user has access to a specific country
  const hasCountryAccess = (countryCode: string | null): boolean => {
    if (isSuperAdmin()) return true;
    if (!countryCode) return true; // Global content
    
    if (isCountryAdmin()) {
      return adminCountries.includes(countryCode);
    }
    
    // For regular users, use their profile country
    return profile?.country_code === countryCode;
  };
  
  // Get the admin's country codes (null for super admins)
  const getAdminCountryCode = (): string | null => {
    if (isSuperAdmin()) return null;
    return profile?.country_code;
  };
  
  // Get all assigned countries for admin
  const getAssignedCountries = (): string[] => {
    if (isSuperAdmin()) return []; // Super admins have access to all
    if (isCountryAdmin()) return adminCountries;
    return profile?.country_code ? [profile.country_code] : [];
  };
  
  return {
    hasCountryAccess,
    getAdminCountryCode,
    getAssignedCountries,
    adminCountries
  };
};
